/**
 * RealisticPlate - Accurate German License Plate
 *
 * Uses real images for EU stars, TÜV sticker, and state seal.
 * Matches the style from wunsch.kennzeichenpro24.de exactly:
 * - Double rounded border (outer gray + inner white gap)
 * - Each input zone in its own light gray rounded box
 * - Seals stacked vertically (TÜV on top, state seal below)
 * - Supports all plate sizes and vehicle types
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  type PlateSize,
  type PlateInput,
  type PlateType,
  type VehicleCategory,
  type PlateRule,
  validateNumbersValue,
  getMaxLettersForCityCode,
  getMaxNumbersForCombo,
  isForbiddenLetterCombination,
} from '@/services/rules/plateRulesEngine'
import { isValidCityCode } from '@/data/germanCityCodes'

// ============================================================================
// TYPES
// ============================================================================

interface RealisticPlateProps {
  plateSize: PlateSize
  plateType: PlateType
  vehicleCategory: VehicleCategory
  input: PlateInput
  interactive?: boolean
  rule?: PlateRule
  onInputChange?: (input: PlateInput) => void
  onCityCodeExit?: (cityCode: string) => void // fires when user moves away from city code zone
  seasonFrom?: number  // 1-12
  seasonTo?: number    // 1-12
  className?: string
}

type ActiveZone = 'cityCode' | 'letters' | 'numbers' | null

// ============================================================================
// EU BAND - Blue strip with real star image + "D"
// ============================================================================

const EUBand = ({ width }: { width: number; height: number }) => {
  const dSize = Math.max(width * 0.55, 12)
  const starsPad = Math.max(width * 0.08, 2)

  return (
    <div
      className="flex-shrink-0 flex flex-col items-center justify-center gap-0"
      style={{
        width: `${width}px`,
        height: '100%',
        background: 'linear-gradient(180deg, #003399 0%, #002266 100%)',
        borderRadius: '5px 0 0 5px',
        padding: `${starsPad}px`,
      }}
    >
      <img
        src="/assets/Stars.svg"
        alt="EU"
        style={{
          width: `${width * 0.78}px`,
          height: `${width * 0.78}px`,
          objectFit: 'contain',
        }}
      />
      <span
        className="text-white font-black leading-none"
        style={{ fontSize: `${dSize}px`, marginTop: `${width * 0.1}px` }}
      >
        D
      </span>
    </div>
  )
}

// ============================================================================
// SEALS - Real images stacked vertically (TÜV on top, state seal below)
// ============================================================================

const Seals = ({ size, horizontal = false }: { size: number; horizontal?: boolean }) => (
  <div
    className={`flex items-center flex-shrink-0 ${horizontal ? 'flex-row' : 'flex-col'}`}
    style={{ gap: `${size * (horizontal ? 0.15 : 0.12)}px` }}
  >
    <img
      src="/assets/01.svg"
      alt="TÜV"
      className="rounded-full"
      style={{ width: `${size}px`, height: `${size}px`, objectFit: 'cover' }}
    />
    <img
      src="/assets/02.webp"
      alt="Seal"
      className="rounded-full"
      style={{ width: `${size * 1.05}px`, height: `${size * 1.05}px`, objectFit: 'cover' }}
    />
  </div>
)

// ============================================================================
// ZONE BOX - Light gray rounded rectangle for each input section
// ============================================================================

const ZoneBox = ({
  children,
  active,
  arrowSize,
  style,
  animated = true,
}: {
  children: React.ReactNode
  active?: boolean
  arrowSize?: number
  style?: React.CSSProperties
  animated?: boolean
}) => {
  const baseStyle: React.CSSProperties = {
    position: 'relative',
    background: active ? '#fff9e6' : '#f0f0f0',
    borderRadius: '3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    transition: 'background 0.2s, outline-color 0.2s',
    outline: active ? '1.5px solid #e6a817' : '1px solid #d0d0d0',
    outlineOffset: '0px',
    animation: active ? 'zoneGlow 1.8s ease-in-out infinite' : 'none',
    ...style,
  }

  // Static mode: plain divs (avoids Framer Motion DOM crash with conditional children)
  if (!animated) {
    return (
      <div style={baseStyle}>
        <div className="flex items-center justify-center w-full">{children}</div>
      </div>
    )
  }

  return (
    <motion.div
      layout
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      style={baseStyle}
    >
      {active && arrowSize && (
        <div
          style={{
            position: 'absolute',
            top: `${-(arrowSize * 0.8 + 10)}px`,
            left: '50%',
            animation: 'arrowBounce 1.8s ease-in-out infinite',
            filter: 'drop-shadow(0 0 8px rgba(230, 168, 23, 0.7))',
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          <svg
            width={arrowSize * 1.4}
            height={arrowSize * 0.9}
            viewBox="0 0 24 16"
            fill="none"
          >
            <path d="M3 3L12 13L21 3" stroke="#e6a817" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
      <motion.div layout="position" className="flex items-center justify-center w-full">
        {children}
      </motion.div>
    </motion.div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const RealisticPlate = ({
  plateSize,
  plateType,
  vehicleCategory,
  input,
  interactive = false,
  rule,
  onInputChange,
  onCityCodeExit,
  seasonFrom = 4,
  seasonTo = 10,
  className = '',
}: RealisticPlateProps) => {

  const [activeZone, setActiveZone] = useState<ActiveZone>(interactive ? 'cityCode' : null)
  const [valMsg, setValMsg] = useState<string | null>(null)

  const ccRef = useRef<HTMLInputElement>(null!)
  const ltRef = useRef<HTMLInputElement>(null!)
  const nmRef = useRef<HTMLInputElement>(null!)

  // ── Layout ───────────────────────────────────────────────────────────────
  const isOneLine = plateSize.layout === 'einzeilig'
  // isTall = significantly taller than wide (true vertical plates).
  // Motorcycle 180x200 is slightly taller than wide but uses standard two-line layout.
  const isTall = plateSize.height > plateSize.width * 1.2
  const isSquare = !isTall && !isOneLine

  // Base scale: 520mm standard plate → 560px display width
  const SCALE = 560 / 520
  const W = plateSize.width * SCALE
  const H = plateSize.height * SCALE

  // Gap ratio: only gaps shrink on narrower plates, NOT fonts/seals
  const refWidth = isOneLine ? 520 : isTall ? 220 : 340
  const gapRatio = Math.max(plateSize.width / refWidth, 0.5)

  const pad = 3
  const innerBorder = 2
  const contentH = H - pad * 2 - innerBorder * 2
  const innerH = contentH

  // EU band: 45mm on standard plates, smaller on compact plates (≤130mm height)
  const euW = (vehicleCategory === 'TRAKTOR' || vehicleCategory === 'LEICHTKRAFT' ? 32 : 45) * SCALE

  // Font: real DIN plate font = 75mm for standard, 49mm for small zweizeilig
  // Font sizes based on real DIN plate standards:
  // - einzeilig: 75mm
  // - zweizeilig 130mm tall (Leichtkraft/Traktor "Schrift verkleinert"): 45mm
  // - zweizeilig motorcycle: scales with width (180=40, 200=45, 220=49)
  // - zweizeilig 200mm tall PKW/Truck (280-340 x 200): 75mm
  const realFs = isOneLine
    ? 75
    : plateSize.height <= 130
      ? 45
      : vehicleCategory === 'MOTORCYCLE'
        ? 49
        : 75
  const fs = realFs * SCALE

  // Seals: fixed physical size
  const sealSz = (plateSize.height <= 130 ? 20 : 26) * SCALE
  const charW = fs * 0.55

  // Only gaps scale down on narrower plates
  const gapUnit = SCALE * Math.min(gapRatio, 1)

  // Zone height: shorter than plate to show plate background around it
  const zoneH = innerH * 0.78

  // ── Hooks ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (interactive) setTimeout(() => { ccRef.current?.focus(); setActiveZone('cityCode') }, 100)
  }, [interactive])

  // Fire onCityCodeExit once when user leaves the city code zone
  const prevActiveRef = useRef<ActiveZone>(null)
  useEffect(() => {
    if (prevActiveRef.current === 'cityCode' && activeZone !== 'cityCode') {
      onCityCodeExit?.(input.cityCode || '')
    }
    prevActiveRef.current = activeZone
  }, [activeZone, input.cityCode, onCityCodeExit])

  // Auto-advance between zones: only fires when user TYPES to fill a zone,
  // not when they click back to edit a full zone
  const prevCCLenRef = useRef(0)
  const prevLTLenRef = useRef(0)
  useEffect(() => {
    if (!interactive || !rule) return
    const cc = input.cityCode || ''
    const lt = input.letters || ''

    // Advance city → letters only if city length just GREW to max
    if (activeZone === 'cityCode'
      && cc.length === rule.maxCityCodeLength
      && cc.length > prevCCLenRef.current
      && isValidCityCode(cc)
      && !lt) {
      setTimeout(() => { setActiveZone('letters'); ltRef.current?.focus() }, 200)
    }

    // Advance letters → numbers only if letters just GREW to max
    if (activeZone === 'letters') {
      const maxL = getMaxLettersForCityCode(rule, cc.length)
      if (lt.length >= maxL && lt.length > 0 && lt.length > prevLTLenRef.current && !input.numbers) {
        setTimeout(() => { setActiveZone('numbers'); nmRef.current?.focus() }, 200)
      }
    }

    prevCCLenRef.current = cc.length
    prevLTLenRef.current = lt.length
  }, [input.cityCode, input.letters, input.numbers, activeZone, rule, interactive])

  // Auto-trim when per-zone limits shrink (e.g. city code length changes letter/number limits)
  useEffect(() => {
    if (!interactive || !onInputChange || !rule) return
    const cc = input.cityCode || ''
    const lt = input.letters || ''
    const nm = input.numbers || ''

    let newLT = lt, newNM = nm

    // Respect per-zone rules only
    if (cc.length > 0) {
      const maxLT = getMaxLettersForCityCode(rule, cc.length)
      if (newLT.length > maxLT) newLT = newLT.slice(0, maxLT)
    }
    if (cc.length > 0 && newLT.length > 0) {
      const maxNM = getMaxNumbersForCombo(rule, cc.length, newLT.length)
      if (newNM.length > maxNM) newNM = newNM.slice(0, maxNM)
    }

    if (newLT !== lt || newNM !== nm) {
      onInputChange({ cityCode: cc, letters: newLT, numbers: newNM })
    }
  }, [input.cityCode, input.letters, rule, onInputChange, interactive])

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleCC = useCallback((v: string) => {
    if (!rule || !onInputChange) return
    const u = v.toUpperCase().replace(/[^A-ZÄÖÜ?]/g, '')
    // Per-zone limit: city code max 3 (from API spec)
    if (!u.includes('?') && u.length > rule.maxCityCodeLength) return

    onInputChange({ ...input, cityCode: u })

    if (u.includes('?')) { setValMsg(null); return }

    if (u.length >= rule.minCityCodeLength && !isValidCityCode(u)) {
      setValMsg(`"${u}" ungültig`)
    } else {
      setValMsg(null)
    }
  }, [rule, input, onInputChange])

  const handleLT = useCallback((v: string) => {
    if (!rule || !onInputChange) return
    const u = v.toUpperCase().replace(/[^A-Z?]/g, '')

    if (u.includes('?')) {
      // Per-zone wildcard limit: max 2 letters
      if (u.length > 2) return
      onInputChange({ ...input, letters: u }); setValMsg(null); return
    }

    const cc = input.cityCode || ''
    if (cc.length < rule.minCityCodeLength) return
    if (!cc.includes('?') && !isValidCityCode(cc)) return

    // Per-zone limit: letters max depends on city code length (rules engine)
    const maxLetters = getMaxLettersForCityCode(rule, cc.length)
    if (u.length > maxLetters) return

    if (u.length >= 2) {
      const forbidden = isForbiddenLetterCombination(u)
      if (forbidden) { setValMsg(forbidden); return }
    }

    onInputChange({ ...input, letters: u })
    setValMsg(null)
  }, [rule, input, onInputChange])

  const handleNM = useCallback((v: string) => {
    if (!rule || !onInputChange) return
    const n = v.replace(/[^0-9?]/g, '')

    if (n.includes('?')) {
      // Per-zone wildcard limit: max 4 numbers
      if (n.length > 4) return
      onInputChange({ ...input, numbers: n }); setValMsg(null); return
    }

    const cc = input.cityCode || ''
    if (cc.length < rule.minCityCodeLength) return

    const lt = input.letters || ''
    if (lt.length === 0) return

    // Per-zone limit: numbers max depends on city + letters combo (rules engine)
    const maxNumbers = getMaxNumbersForCombo(rule, cc.length, lt.length)
    if (n.length > maxNumbers) return

    const ni = { ...input, numbers: n }
    const r = validateNumbersValue(rule, ni)
    if (r.valid) { onInputChange(ni); setValMsg(null) }
    else { setValMsg(r.error || 'Ungültig') }
  }, [rule, input, onInputChange])

  // ── Values ───────────────────────────────────────────────────────────────
  const cc = input.cityCode || ''
  const lt = input.letters || ''
  const nm = input.numbers || ''
  const typeInd = (plateType === 'E' || plateType === 'E_SEASON') ? 'E' : (plateType === 'H' || plateType === 'H_SEASON') ? 'H' : null
  const isSeason = plateType.includes('SEASON')
  const isGreen = plateType === 'GREEN'

  // ── Per-zone limits (from rules engine, which may be derived for E/H/Season) ─
  const maxCC = rule?.maxCityCodeLength || 3
  const maxLT = getMaxLettersForCityCode(rule ?? null, cc.length || 1)
  const maxNM = getMaxNumbersForCombo(rule ?? null, cc.length || 1, lt.length || 1)

  // Total physical capacity of this plate/type combo
  const maxTotal = rule?.maxTotalChars ?? 8

  // Defaults (empty placeholders): pick a valid 2-2-rest combo that fits maxTotal
  const defaultCC = Math.min(2, maxCC, Math.max(maxTotal - 2, 1))
  const defaultLT = Math.min(2, maxLT, Math.max(maxTotal - defaultCC - 1, 1))
  const defaultNM = Math.min(maxNM, Math.max(maxTotal - defaultCC - defaultLT, 1))

  // Actual display widths: typed content length, else defaults
  const ccUsed = cc.length > 0 ? cc.length : defaultCC
  const ltUsed = lt.length > 0 ? lt.length : defaultLT
  const nmDisplay = nm.length > 0 ? nm.length : defaultNM
  const nmPlaceholder = nmDisplay <= 2 ? '12'.slice(0, nmDisplay) : nmDisplay === 3 ? '123' : '1234'
  const ccPlaceholder = defaultCC === 1 ? 'B' : defaultCC === 2 ? 'GÖ' : 'ABG'
  const ltPlaceholder = defaultLT === 1 ? 'A' : 'AB'

  // Gaps shrink when plate fills up (to keep content visible)
  const totalUsed = ccUsed + ltUsed + nmDisplay
  const gap = (totalUsed >= 7 ? 2 : 5) * gapUnit


  // ── Font style ───────────────────────────────────────────────────────────
  // Adjusted to exactly match the very bold, black rendering of original site
  const fontColor = isGreen ? '#1b5e20' : '#111'
  const pf: React.CSSProperties = {
    fontFamily: "'Sauerkrauto', 'Arial Black', sans-serif",
    fontWeight: 'normal',
    fontSize: `${fs}px`,
    letterSpacing: `${fs * 0.01}px`,
    lineHeight: 1,
    color: fontColor,
    transform: 'none', // stretch slightly vertically to match the real plate font proportions
  }

  // ── Render zone input/text ───────────────────────────────────────────────
  const renderZone = (
    ref: React.RefObject<HTMLInputElement>,
    value: string,
    zone: ActiveZone,
    onChange: (v: string) => void,
    maxLen: number,
    placeholder: string,
    disabled?: boolean,
    _minWidth?: number,
    zoneHeightOverride?: number,
  ) => {
    const isActive = activeZone === zone
    const effectiveZoneH = zoneHeightOverride || zoneH
    // Width adapts to actual content, defaults based on plate size
    const defaults = { cityCode: defaultCC, letters: defaultLT, numbers: defaultNM }
    let displayChars = value.length > 0 ? value.length : defaults[zone!] || 2
    // Numbers zone: always use calculated display width
    if (zone === 'numbers') {
      displayChars = nmDisplay
    }
    const baseW = displayChars * charW
    const padX = fs * 0.12
    // Zones use flex-shrink to compress on narrow plates
    const zoneStyle: React.CSSProperties = {
      padding: `0 ${padX}px`,
      height: `${effectiveZoneH}px`,
      minWidth: `${charW * 0.8}px`,
      width: `${baseW + padX * 2}px`,
      flexShrink: 1,
      transition: 'width 0.15s ease',
    }

    if (!interactive) {
      return (
        <ZoneBox style={zoneStyle} animated={false}>
          <span style={pf}>{value || <span style={{ opacity: 0.35 }}>{placeholder}</span>}</span>
        </ZoneBox>
      )
    }

    return (
      <ZoneBox
        active={isActive}
        arrowSize={isActive ? Math.max(fs * 0.25, 12) : undefined}
        style={zoneStyle}
        animated={true}
      >
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            if (zone === 'letters' && cc.length < (rule?.minCityCodeLength || 1)) { ccRef.current?.focus(); return }
            if (zone === 'numbers') {
              if (cc.length < (rule?.minCityCodeLength || 1)) { ccRef.current?.focus(); return }
              if (lt.length === 0) { ltRef.current?.focus(); return }
            }
            setActiveZone(zone)
          }}
          disabled={disabled}
          className="bg-transparent border-none outline-none text-center w-full disabled:opacity-25"
          style={{ ...pf, caretColor: '#333', height: `${fs * 0.85}px`, padding: 0 }}
          maxLength={maxLen}
          autoComplete="off"
          placeholder={placeholder}
        />
      </ZoneBox>
    )
  }

  const renderTypeInd = (heightOverride?: number) => {
    if (!typeInd) return null
    const h = heightOverride || zoneH
    return (
      <ZoneBox style={{ padding: `0 ${fs * 0.1}px`, height: `${h}px` }} animated={false}>
        <span style={pf}>{typeInd}</span>
      </ZoneBox>
    )
  }

  const renderSeason = (heightOverride?: number) => {
    if (!isSeason) return null
    const effectiveH = heightOverride || zoneH
    // Season fraction should fill the zone height, each number ~40% of height
    const numSize = effectiveH * 0.42
    const lineW = numSize * 1.6
    const fromStr = String(seasonFrom).padStart(2, '0')
    const toStr = String(seasonTo).padStart(2, '0')
    const Wrap = interactive ? motion.div : 'div'
    const wrapProps = interactive ? { layout: true } : {}
    const seasonFont: React.CSSProperties = {
      fontFamily: "'Sauerkrauto', 'Arial Black', sans-serif",
      fontWeight: 'normal',
      fontSize: `${numSize}px`,
      color: fontColor,
    }
    return (
      <Wrap {...wrapProps} className="flex flex-col items-center justify-center flex-shrink-0" style={{ height: `${effectiveH}px`, lineHeight: 1 }}>
        <span style={seasonFont}>{fromStr}</span>
        <div style={{ width: `${lineW}px`, height: '2px', background: fontColor, margin: `${effectiveH * 0.03}px 0` }} />
        <span style={seasonFont}>{toStr}</span>
      </Wrap>
    )
  }

  // Flexible spacer to distribute space evenly across the plate
  const Spacer = ({ animated = false }: { animated?: boolean }) => {
    if (animated) return <motion.div layout style={{ flexGrow: 1, minWidth: `${gap}px` }} />
    return <div style={{ flexGrow: 1, minWidth: `${gap}px` }} />
  }

  const layoutOneLine = () => {
    if (!interactive) {
      return (
        <div className="flex items-center h-full flex-1" style={{ padding: `0 ${gap + 8}px 0 ${gap * 1.5}px` }}>
          {renderZone(ccRef, cc, 'cityCode', handleCC, maxCC, ccPlaceholder, false, 2)}
          <Spacer />
          <div className="flex-shrink-0"><Seals size={sealSz} /></div>
          <Spacer />
          {renderZone(ltRef, lt, 'letters', handleLT, maxLT, ltPlaceholder, true, 2)}
          <Spacer />
          {renderZone(nmRef, nm, 'numbers', handleNM, maxNM, nmPlaceholder, true, 4)}
          {typeInd && <Spacer />}
          {renderTypeInd()}
          {renderSeason()}
        </div>
      )
    }
    return (
      <motion.div layout className="flex items-center h-full flex-1" style={{ padding: `0 ${gap + 4}px 0 ${gap * 1.5}px` }}>
        {renderZone(ccRef, cc, 'cityCode', handleCC, maxCC, ccPlaceholder, false, 2)}
        <Spacer animated />
        <motion.div layout className="flex-shrink-0"><Seals size={sealSz} /></motion.div>
        <Spacer animated />
        {renderZone(ltRef, lt, 'letters', handleLT, maxLT, ltPlaceholder, cc.length < (rule?.minCityCodeLength || 1), 2)}
        <Spacer animated />
        {renderZone(nmRef, nm, 'numbers', handleNM, maxNM, nmPlaceholder, cc.length < (rule?.minCityCodeLength || 1) || lt.length === 0, 4)}
        {typeInd && <Spacer animated />}
        {renderTypeInd()}
        {renderSeason()}
      </motion.div>
    )
  }

  const layoutTwoLine = () => {
    const lettersMinW = maxLT
    const numbersMinW = maxNM

    // Small plates (≤130mm height) AND motorcycle plates: seals are horizontal (side by side)
    const isSmallPlate = plateSize.height <= 130
    const sealsHorizontal = isSmallPlate || vehicleCategory === 'MOTORCYCLE'

    // Row heights: each row gets ~48% of content height
    const rowH = contentH * 0.48
    // Zone height: tied to font size so smaller fonts get tighter zones
    // (font + ~25% breathing room, capped by row height)
    const rowZoneH = Math.min(rowH * 0.85, fs * 1.25)
    const rowGap = contentH * 0.04

    // Season placement: 200mm plates → row 1 (after seals), 130mm plates → row 2 (after type indicator)
    const seasonOnRow1 = !isSmallPlate

    // Motorcycle: city code on top (prominent), seals small below
    const isMotor = vehicleCategory === 'MOTORCYCLE'
    const ccZoneH = isMotor ? rowZoneH * 0.72 : rowZoneH
    const sealsRowH = isMotor ? rowZoneH * 0.32 : 0

    const renderRow1Content = () => {
      if (isMotor) {
        return (
          <div className="flex flex-1 items-stretch" style={{ padding: `${rowH * 0.05}px ${gap}px ${rowH * 0.05}px` }}>
            {/* Middle column: city code on top, seals below */}
            <div className="flex flex-col items-center justify-between flex-1">
              {renderZone(ccRef, cc, 'cityCode', handleCC, maxCC, ccPlaceholder, false, 1.5, ccZoneH)}
              <div style={{ height: `${sealsRowH}px`, alignSelf: 'flex-start', marginLeft: `${sealsRowH * 0.5}px` }} className="flex items-center">
                <Seals size={sealsRowH * 0.95} horizontal />
              </div>
            </div>
            {/* Right column: season fraction aligned to seals level (bottom) */}
            {seasonOnRow1 && (
              <div className="flex items-end flex-shrink-0" style={{ marginRight: `${gap * 1.5}px` }}>
                {renderSeason(sealsRowH * 2.2)}
              </div>
            )}
          </div>
        )
      }
      return (
        <div className="flex items-center flex-1" style={{ gap: `${gap}px`, padding: `0 ${gap}px` }}>
          <div className="flex items-center flex-1 justify-center" style={{ gap: `${gap}px` }}>
            {renderZone(ccRef, cc, 'cityCode', handleCC, maxCC, ccPlaceholder, false, 1.5, rowZoneH)}
            <Seals size={sealSz} horizontal={sealsHorizontal} />
          </div>
          {seasonOnRow1 && <div className="flex-shrink-0">{renderSeason(rowZoneH)}</div>}
        </div>
      )
    }

    if (!interactive) {
      return (
        <div className="flex flex-col h-full w-full" style={{ gap: `${rowGap}px` }}>
          {/* Row 1: EU band + city code (+ seals below for motor, or beside for others) */}
          <div className="flex items-stretch" style={{ height: `${rowH}px` }}>
            <EUBand width={euW} height={rowH} />
            {renderRow1Content()}
          </div>
          {/* Row 2: letters + numbers (+ season for 130mm) */}
          <div className="flex items-center h-full flex-1" style={{ height: `${rowH}px`, padding: `0 ${plateSize.width <= 200 ? gap : gap + 8}px` }}>
            {renderZone(ltRef, lt, 'letters', handleLT, maxLT, ltPlaceholder, true, lettersMinW, rowZoneH)}
            <Spacer />
            {renderZone(nmRef, nm, 'numbers', handleNM, maxNM, nmPlaceholder, true, numbersMinW, rowZoneH)}
            {typeInd && <Spacer />}
            {renderTypeInd(rowZoneH)}
            {!seasonOnRow1 && renderSeason(rowZoneH)}
          </div>
        </div>
      )
    }
    return (
      <motion.div layout className="flex flex-col h-full w-full" style={{ gap: `${rowGap}px` }}>
        {/* Row 1 */}
        <motion.div layout className="flex items-stretch" style={{ height: `${rowH}px` }}>
          <EUBand width={euW} height={rowH} />
          {renderRow1Content()}
        </motion.div>
        {/* Row 2: letters + numbers (+ season for 130mm) */}
        <motion.div layout className="flex items-center h-full flex-1" style={{ height: `${rowH}px`, padding: `0 ${plateSize.width <= 200 ? gap : gap + 8}px` }}>
          {renderZone(ltRef, lt, 'letters', handleLT, maxLT, ltPlaceholder, cc.length < (rule?.minCityCodeLength || 1), lettersMinW, rowZoneH)}
          <Spacer animated />
          {renderZone(nmRef, nm, 'numbers', handleNM, maxNM, nmPlaceholder, cc.length < (rule?.minCityCodeLength || 1) || lt.length === 0, numbersMinW, rowZoneH)}
          {typeInd && <Spacer animated />}
          {renderTypeInd(rowZoneH)}
          {!seasonOnRow1 && renderSeason(rowZoneH)}
        </motion.div>
      </motion.div>
    )
  }

  const layoutVertical = () => {
    if (!interactive) {
      return (
        <div className="flex flex-col items-center justify-center h-full flex-1" style={{ gap: `${gap}px`, padding: `${gap}px` }}>
          {/* Row 1: city code + season (side by side) */}
          <div className="flex items-center" style={{ gap: `${gap}px` }}>
            {renderZone(ccRef, cc, 'cityCode', handleCC, maxCC, ccPlaceholder, false, 1.5)}
            {renderSeason()}
          </div>
          <Seals size={sealSz} horizontal />
          {/* Row 3: letters + numbers + type indicator */}
          <div className="flex items-center" style={{ gap: `${gap}px` }}>
            {renderZone(ltRef, lt, 'letters', handleLT, maxLT, ltPlaceholder, true, 1.5)}
            {renderZone(nmRef, nm, 'numbers', handleNM, maxNM, nmPlaceholder, true, 2)}
            {renderTypeInd()}
          </div>
        </div>
      )
    }
    return (
      <motion.div layout className="flex flex-col items-center justify-center h-full flex-1" style={{ gap: `${gap}px`, padding: `${gap}px` }}>
        {/* Row 1: city code + season (side by side) */}
        <motion.div layout className="flex items-center" style={{ gap: `${gap}px` }}>
          {renderZone(ccRef, cc, 'cityCode', handleCC, maxCC, ccPlaceholder, false, 1.5)}
          {renderSeason()}
        </motion.div>
        <Seals size={sealSz} horizontal />
        {/* Row 3: letters + numbers + type indicator */}
        <motion.div layout className="flex items-center" style={{ gap: `${gap}px` }}>
          {renderZone(ltRef, lt, 'letters', handleLT, maxLT, ltPlaceholder, cc.length < (rule?.minCityCodeLength || 1), 1.5)}
          {renderZone(nmRef, nm, 'numbers', handleNM, maxNM, nmPlaceholder, cc.length < (rule?.minCityCodeLength || 1) || lt.length === 0, 2)}
          {renderTypeInd()}
        </motion.div>
      </motion.div>
    )
  }

  // ── Arrow size for active zone ──────────────────────────────────────────
  const arrowH = Math.max(fs * 0.38, 14)
  const arrowSpace = interactive ? arrowH + 35 : 5

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className={`relative ${className}`} style={{ paddingTop: `${arrowSpace}px` }}>
      {/* Keyframe animations for arrow */}
      <style>{`
        @keyframes arrowBounce {
          0%, 100% { transform: translateX(-50%) translateY(0); opacity: 0.7; }
          50% { transform: translateX(-50%) translateY(6px); opacity: 1; }
        }
        @keyframes zoneGlow {
          0%, 100% { box-shadow: 0 0 4px rgba(255, 180, 0, 0.3), inset 0 0 2px rgba(255, 180, 0, 0.1); }
          50% { box-shadow: 0 0 20px rgba(255, 180, 0, 0.9), 0 0 40px rgba(255, 150, 0, 0.5), inset 0 0 8px rgba(255, 180, 0, 0.3); }
        }
      `}</style>

      {/* Plate - realistic German license plate */}
      <div
        className="mx-auto select-none"
        style={{
          width: `${W}px`,
          height: `${H}px`,
          maxWidth: '100%',
          border: '1.5px solid #222',
          borderRadius: '8px',
          padding: `${pad}px`,
          background: '#f8f9fa',
          boxShadow: '0 0 0 3px #dadada, 0 4px 12px rgba(0,0,0,0.1)',
          overflow: 'visible',
        }}
      >
        {/* Inner border frame - characteristic of real German plates */}
        <div
          style={{
            width: '100%',
            height: '100%',
            border: '2px solid rgba(0,0,0,0.85)',
            borderRadius: '5px',
            overflow: 'visible',
          }}
        >
          {/* Plate content */}
          <div
            className="flex"
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '2px',
              background: 'linear-gradient(175deg, #fefefe 0%, #f8f6f0 40%, #f5f2ec 100%)',
              overflow: 'visible',
            }}
          >
            {/* EU band: full height for one-line & tall; for two-line (square), it's inside layoutTwoLine per row */}
            {!isSquare && <EUBand width={euW} height={innerH} />}
            {isOneLine ? layoutOneLine() : isTall ? layoutVertical() : layoutTwoLine()}
          </div>
        </div>
      </div>

      {/* Validation */}
      {interactive && valMsg && (
        <div className="mt-2 mx-auto p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs text-center" style={{ maxWidth: `${W}px` }}>
          {valMsg}
        </div>
      )}

      {/* Size */}
      <div className="text-center text-xs text-gray-500" style={{ maxWidth: `${W}px`, margin: '4px auto 0' }}>
        {plateSize.width} x {plateSize.height} mm
        {plateSize.layout === 'zweizeilig' ? ' (Zweizeilig)' : ' (Einzeilig)'}
      </div>
    </div>
  )
}

export default RealisticPlate
